import React, { useEffect, useMemo, useState } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import PaymentModal from "../../components/accounting/PaymentModal";
import {
  deletePayment,
  getcashorbank,
  getPaymentByGuid,
} from "../../services/AccountApiServices";
import { PaymentEntry } from "../../core/json/PaymentEntry";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, money, num } from "../../components/report/reportFormat";

function PaymentMaster() {
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const [ledgerData, setLedgerData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [PaymentData, setPaymentData] = useState([]);
  const [updateData, setUpdateData] = useState([]);

  useEffect(() => {
    handlePaymentList();
    handleLedgerList();
  }, []);

  const handlePaymentList = async () => {
    try {
      const response = await getPaymentByGuid(branch);
      setPaymentData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log(error?.response?.data?.Message || "something went wrong");
      setPaymentData([]);
    }
  };
  const handleLedgerList = async () => {
    try {
      const response = await getcashorbank();
      if (Array.isArray(response?.data)) {
        setLedgerData(
          response?.data?.map((e) => ({
            value: e.guid,
            label: e.name,
          }))
        );
      } else {
        setLedgerData([]);
      }
    } catch (error) {
      console.log(error?.response?.data?.Message || "Something went wrong");
      setLedgerData([]);
    }
  };

  const handleEditClick = (guid) => {
    try {
      const paymentByGuid = PaymentData.find((data) => data.guid === guid);

      setUpdateData(paymentByGuid);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching payment by GUID:",
        error?.response?.data?.Message
      );
    }
  };
  const handleDeleteClick = (obj) => {
    const objpayment = new PaymentEntry();
    objpayment.Branch = branch;
    objpayment.guid = obj.guid;
    objpayment.id = obj.id;
    objpayment.updatedUser = userId?.id;
    objpayment.createdUser = userId?.id;
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deletePayment(objpayment);
          if (response.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The payment has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlePaymentList();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting sales quotation:", error);
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const MySwal = withReactContent(Swal);

  /* ---------------- list ---------------- */
  // one flat record per payment; the keys match the column definitions
  const rows = useMemo(
    () =>
      PaymentData.map((p) => ({
        guid: p.guid,
        raw: p,
        date: dateOf(p.date),
        billNo: p.no != null ? String(p.no) : "",
        cashBank: p.ledgerName || "",
        ledgerID: p.ledgerID,
        narration: p.narration || "",
        items: (p.paymentDetails || []).length,
        net: num(p.totalAmt),
        lines: p.paymentDetails || [],
      })),
    [PaymentData]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry", type: "doc" },
      { k: "cashBank", t: "Cash/Bank", type: "party" },
      { k: "narration", t: "Remarks" },
      { k: "items", t: "Lines", type: "int", off: true },
      { k: "net", t: "Amount", type: "money" },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Payment" }], []);

  const filters = useMemo(
    () => [
      {
        key: "cashBank",
        label: "Cash/Bank",
        items: (ledgerData || []).map((l) => ({ v: l.value, t: l.label || "—" })),
        match: (r, v) => v.includes(r.ledgerID),
      },
    ],
    [ledgerData]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.cashBank) ||
      hit(r.narration) ||
      hit(r.date) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.ledgerName) || hit(l?.narration))
    );
  };

  const drawerContent = (r) => ({
    title: r.cashBank || "Payment",
    subtitle: [`Payment ${r.billNo}`, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.cashBank,
    meta: [
      { k: "Paid from", v: r.cashBank },
      { k: "Remarks", v: r.narration },
      { k: "Lines", v: intg(r.items) },
    ],
    lines: {
      title: "Payment lines",
      columns: [{ t: "Ledger" }, { t: "Remarks" }, { t: "Amount", align: "right" }],
      rows: r.lines.map((l) => [
        <div key="nm" className="axr-nm">{l?.ledgerName || "Unknown ledger"}</div>,
        l?.narration || "—",
        money(l?.amount),
      ]),
    },
    net: { l: "Net total", v: money(r.net) },
  });

  // this page never gated New / Edit / Delete on the menu permission (the
  // checks were commented out before the redesign), so neither does the list
  return (
    <TransactionScreen
      title="Payment"
      subtitle="Manage Payment here"
      unit="payments"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, cash/bank, remarks, ledger, amount…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "net:-1", t: "Largest amount" },
        { v: "net:1", t: "Smallest amount" },
      ]}
      exportName="Payment Master"
      onRefresh={handlePaymentList}
      canAdd
      newLabel="New payment"
      onNew={() => {
        setModalMode("add"); // Set the modal mode to 'add'
        setModalOpen(true); // Open the modal
      }}
      canEdit
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit payment"
      canDelete
      onDelete={(r) => handleDeleteClick(r.raw)}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <PaymentModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlePaymentList();
          }}
        />
      )}
    </TransactionScreen>
  );
}

export default PaymentMaster;
